import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react';
import { Newsletter } from '../common/Newsletter';
import { useSiteSettings } from '../../hooks/useSiteSettings';

export const Footer = () => {
  const { t } = useTranslation();
  const { getSetting } = useSiteSettings();

  // Get social media URLs from settings
  const socialLinks = [
    { platform: 'facebook', url: getSetting('social_facebook'), icon: Facebook },
    { platform: 'instagram', url: getSetting('social_instagram'), icon: Instagram },
    { platform: 'twitter', url: getSetting('social_twitter'), icon: Twitter }
  ].filter(link => link.url && link.url.trim() !== ''); // Only show if URL is set
  return (
    <footer className="bg-muted/30 border-t mt-20">
      {/* Newsletter Section */}
      <div className="container-custom py-12">
        <Newsletter />
      </div>

      <div className="container-custom py-12 border-t">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">NC</span>
              </div>
              <span className="font-serif text-2xl font-bold text-foreground">
                NaturalCare
              </span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your trusted source for natural cosmetics and pharmaceutical products. Quality, safety, and care in every product.
            </p>
            <div className="flex items-center space-x-4 mt-4">
              {socialLinks.map(({ platform, url, icon: Icon }) => (
                <a 
                  key={platform}
                  href={url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-smooth"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/shop" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('footer.shopAll')}
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('nav.categories')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-4">{t('footer.customerService')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('footer.faq')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('footer.termsConditions')}
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  {t('footer.productDisclaimer')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-4">{t('footer.contactUs')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">
                  123 Natural Street, Wellness City, WC 12345
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground">+1 (555) 123-4567</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground">support@naturalcare.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t mt-8 pt-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} NaturalCare. All rights reserved. Premium natural cosmetics and pharmaceutical products.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Medical Disclaimer: Products sold are cosmetics and OTC pharmaceuticals. Not for prescription use.
          </p>
        </div>
      </div>
    </footer>
  );
};